import React from 'react'

const CheckAvailabilityPreview = ({ data }: any) => {
  const [openIndex, setOpenIndex] = React.useState<number | null>(0);

  // Handle both single invoice and array of invoices
  const invoices = Array.isArray(data) ? data : (data ? [data] : []);

  if (invoices.length === 0) {
    return <div>No invoice data available</div>;
  }

  // If only one invoice, render directly
  if (invoices.length === 1) {
    return <InvoiceComponent data={invoices[0]} invoiceIndex={0} totalInvoices={1} />;
  }

  // Multiple invoices - use accordion format
  return (
    <div className="accordion" id="invoiceAccordion">
      {invoices.map((invoice: any, index: number) => {
        const startLabel = invoice.billStartDate
          ? new Date(invoice.billStartDate).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })
          : '';
        const endLabel = invoice.billEndDate
          ? new Date(invoice.billEndDate).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })
          : '';
        const total = invoice.totals?.total?.final != null
          ? `${invoice.totals.currency ?? ''} ${Number(invoice.totals.total.final).toFixed(2)}`
          : '';

        const isOpen = openIndex === index;
        return (
          <div
            className="accordion-item"
            key={index}
            style={{ borderRadius: 0, border: 'none', borderBottom: '1px solid #dee2e6' }}
          >
            <h2 className="accordion-header" id={`invoiceHeading${index}`} style={{ margin: 0 }}>
              <button
                className={`accordion-button${isOpen ? '' : ' collapsed'}`}
                type="button"
                onClick={() => setOpenIndex(isOpen ? null : index)}
                aria-expanded={isOpen ? 'true' : 'false'}
                aria-controls={`invoiceCollapse${index}`}
                style={{
                  fontWeight: 600,
                  fontSize: '13px',
                  padding: '10px 16px',
                  background: '#e8f0fe',
                  boxShadow: 'none',
                  borderRadius: 0,
                }}
              >
                <span style={{ marginRight: '14px', color: '#555', minWidth: '80px' }}>
                  Invoice {index + 1}
                </span>
                <span style={{ color: '#333' }}>
                  {startLabel} &nbsp;→&nbsp; {endLabel}
                </span>
                {total && (
                  <span style={{ marginLeft: 'auto', marginRight: '32px', color: '#1a73e8', fontWeight: 700 }}>
                    {total}
                  </span>
                )}
              </button>
            </h2>
            <div
              className={`accordion-collapse collapse${isOpen ? ' show' : ''}`}
              id={`invoiceCollapse${index}`}
            >
              <div style={{ padding: 0, margin: 0 }}>
                <InvoiceComponent data={invoice} invoiceIndex={index} totalInvoices={invoices.length} />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const InvoiceComponent = ({ data, invoiceIndex, totalInvoices }: any) => {
  const d = data;
  const currency = d.totals?.currency ?? 'USD';

  const fmt = (n: any, ccy = currency) =>
    `${ccy} ${(Number(n ?? 0)).toFixed(2)}`;

  const formatDate = (s?: string) => {
    if (!s) return '';
    const dt = new Date(s);
    return dt.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
  };

  const formatDateLong = (s?: string) => {
    if (!s) return '';
    const dt = new Date(s);
    return dt.toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const invoiceDate = d.invoiceDate ?? new Date().toISOString();
  const startDate = d.billStartDate;
  const endDate = d.billEndDate;

  const rentalSummary = d.rental_summary || {};
  const durationObj = rentalSummary.duration || {};

  // Left Summary from rental_summary
  const summaryItems = [
    { label: 'Duration:', value: `${durationObj.value ?? ''} ${durationObj.unit ?? ''}` },
    { label: 'Rate:', value: rentalSummary.rate },
    { label: 'Estimated Daily Hours:', value: rentalSummary.estimated_daily_hours },
    { label: 'Estimate Total Hours:', value: rentalSummary.estimate_total_hours },
    { label: 'Engine Rate:', value: rentalSummary.engine_rate },
    { label: 'Liability Insurance:', value: rentalSummary.liability_insurance },
    { label: 'Physical Insurance:', value: rentalSummary.physical_insurance },
    { label: 'Administration Fees:', value: rentalSummary.administration_fees },
  ].filter(item => 
    item.value !== null && 
    item.value !== undefined && 
    item.value !== '0.00' && 
    item.value !== 0 && 
    item.value !== '0' && 
    !String(item.value).startsWith('0 ') && 
    String(item.value).trim() !== ''
  );

  // Right Side Table: actualFields + customFields
  const tableItems = [
    ...(d.actualFields ?? []).map((f: any) => ({
      label: f?.fieldName,
      unit: f?.unit ?? '-',
      qty: f?.qty ?? '-',
      rate: f?.rate,
      initial: f?.initialCharge,
      adj: f?.adjustment,
      final: f?.finalAmount,
    })),
    ...(d.customFields ?? []).map((f: any) => ({
      label: f?.fieldName,
      unit: f?.unit ?? '-',
      qty: f?.qty ?? '-',
      rate: f?.rate,
      initial: f?.initial_charge,
      adj: f?.adjustment,
      final: f?.final,
    })),
  ].filter(item => 
    item.final !== null && 
    item.final !== undefined && 
    item.final !== '0.00' && 
    item.final !== 0 && 
    item.final !== '0'
  );

  const TD: React.CSSProperties = {
    border: '1px solid #999',
    padding: '5px 7px',
    fontSize: '11px',
    verticalAlign: 'top',
  };
  const TH: React.CSSProperties = {
    ...TD,
    fontWeight: 'bold',
    background: '#f5f5f5',
    textAlign: 'center',
  };

  const totals = d.totals || {};

  return (
    <>
      <style>{`
        .cap-inv { font-family: Arial, Helvetica, sans-serif; font-size: 12px; background: #fff; padding: 18px 22px; }
        .cap-inv table { border-collapse: collapse; }
        .cap-inv .lbl { font-size: 11px; color: #555; }
        .cap-inv .bold14 { font-weight: 700; font-size: 13px; }
        .cap-inv .right { text-align: right; }
        .cap-inv .center { text-align: center; }
      `}</style>

      <div className="cap-inv">
        {/* ── HEADER ── */}
        <table style={{ width: '100%' }}>
          <tbody>
            <tr>
              <td style={{ width: '50%', verticalAlign: 'top' }}>
                <div className="lbl">Issue Date</div>
                <div style={{ fontWeight: 600 }}>{formatDateLong(invoiceDate)}</div>
                <br />
                <div className="lbl">Billed To:</div>
                <div className="bold14">{d.renterName || ''}</div>
                <div>{d.renterAddress || ''}</div>
              </td>
              <td style={{ width: '50%', verticalAlign: 'top', textAlign: 'right' }}>
                <img
                  src="../images/vHub-black.png"
                  alt="vHub Logo"
                  style={{ width: '120px', height: 'auto', objectFit: 'contain', marginBottom: '4px' }}
                />
                <div style={{ fontWeight: 700, fontSize: '12px' }}>vhub, a division of Finloc 2000 Inc</div>
                <div className="lbl">11505, 1ere ave, bureau 500 Saint-</div>
                <div className="lbl">Georges, QC G5Y 7X3 Canada</div>
                <div className="lbl" style={{ marginTop: '4px' }}>1 (866) 849-0189</div>
                <div className="lbl">www.vhubapp.com</div>
              </td>
            </tr>
          </tbody>
        </table>

        {/* ── INFO ROW ── */}
        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '10px' }}>
          <tbody>
            <tr>
              <td style={{ ...TD, width: '25%' }}>
                <strong>Invoice Number:</strong> {d.contractNumber ? `F-${d.contractNumber}-1` : '—'}<br />
                <strong>Reservation Code:</strong> {d.contractNumber || '—'}<br />
              </td>
              <td style={{ ...TD, width: '25%' }}>
                <strong>Owner:</strong> {d.owner ? `${d.owner}` : '—'}<br />
                <strong>Unit Number:</strong> {d.unitNumber || '—'}<br />
              </td>
              <td style={{ ...TD, width: '25%' }}>
                <strong>VIN:</strong> {d.vin || '—'}<br />
                <strong>Year:</strong> {d.year || '—'}
              </td>
              <td style={{ ...TD, width: '25%' }}>
                <strong>Equipment Type:</strong> {d.equipmentType || '—'}
              </td>
            </tr>
          </tbody>
        </table>

        {/* ── SIDE-BY-SIDE ── */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0', marginBottom: '6px' }}>
          {/* LEFT — summary sidebar */}
          <table style={{ width: '15%', borderCollapse: 'collapse', flexShrink: 0, fontSize: '11px' }}>
            <tbody>
              <tr><td colSpan={2} style={{ padding: '6px 0', fontWeight: 700, fontSize: '11px', background: 'none', border: 'none' }}>&nbsp;</td></tr>
              {summaryItems.map((item, i) => (
                <tr key={i}>
                  <td style={{ padding: '3px 6px', color: '#333', whiteSpace: 'nowrap' }}>{item.label}</td>
                  <td style={{ padding: '3px 6px', textAlign: 'right', whiteSpace: 'nowrap' }}>{item.value}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* RIGHT — charges table */}
          <table style={{ width: '85%', borderCollapse: 'collapse', border: '1px solid #999' }}>
            <thead>
              <tr>
                <th style={{ ...TH, width: '18%', textAlign: 'left', border: 'none', borderBottom: '1px solid #999' }}>Date</th>
                <th style={{ ...TH, width: '20%', textAlign: 'left', border: 'none', borderBottom: '1px solid #999' }}>Description</th>
                <th style={{ ...TH, width: '8%',  border: 'none', borderBottom: '1px solid #999', borderLeft: '1px solid #999' }}>Unit</th>
                <th style={{ ...TH, width: '8%',  border: 'none', borderBottom: '1px solid #999' }}>Qty</th>
                <th style={{ ...TH, width: '8%', textAlign: 'right',  border: 'none', borderBottom: '1px solid #999' }}>Rate</th>
                <th style={{ ...TH, width: '13%', textAlign: 'right', border: 'none', borderBottom: '1px solid #999', borderLeft: '1px solid #999' }}>Initial Charge</th>
                <th style={{ ...TH, width: '13%', textAlign: 'right', border: 'none', borderBottom: '1px solid #999' }}>Adjustment</th>
                <th style={{ ...TH, width: '12%', textAlign: 'right', border: 'none', borderBottom: '1px solid #999' }}>Final</th>
              </tr>
            </thead>
            <tbody>
              {tableItems.map((item, i) => {
                const rowBorder = '1px solid #e5e5e5';
                return (
                  <tr key={i}>
                    {i === 0 && (
                      <td
                        style={{ padding: '4px 6px', fontSize: '11px', textAlign: 'start', verticalAlign: 'top', borderRight: '1px solid #999', borderBottom: rowBorder }}
                        rowSpan={tableItems.length}
                      >
                        {formatDate(startDate)} to {formatDate(endDate)}
                      </td>
                    )}
                    <td style={{ padding: '3px 6px', fontSize: '11px', borderBottom: rowBorder }}>{item.label}</td>
                    <td style={{ padding: '3px 6px', fontSize: '11px', textAlign: 'center', borderBottom: rowBorder, borderLeft: '1px solid #999' }}>{item.unit}</td>
                    <td style={{ padding: '3px 6px', fontSize: '11px', textAlign: 'center', borderBottom: rowBorder }}>{item.qty}</td>
                    <td style={{ padding: '3px 6px', fontSize: '11px', textAlign: 'right',  borderBottom: rowBorder }}>{item.rate}</td>
                    <td style={{ padding: '3px 6px', fontSize: '11px', textAlign: 'right',  borderBottom: rowBorder, borderLeft: '1px solid #999' }}>{item.initial}</td>
                    <td style={{ padding: '3px 6px', fontSize: '11px', textAlign: 'right',  borderBottom: rowBorder }}>{item.adj}</td>
                    <td style={{ padding: '3px 6px', fontSize: '11px', textAlign: 'right',  borderBottom: rowBorder, fontWeight: 600 }}>{item.final}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              {[
                { label: 'SUB TOTAL:', data: totals.sub_total },
                { label: 'TAX:',       data: totals.tax },
                { label: 'TOTAL:',     data: totals.total },
              ].map((row, idx) => (
                <tr key={idx}>
                  <td colSpan={2} style={{ border: 'none', padding: '3px 6px', fontSize: '11px', textAlign: 'right' }} />
                  <td style={{ border: 'none', padding: '3px 6px', fontSize: '11px', borderLeft: '1px solid #999' }} />
                  <td colSpan={2} style={{ border: 'none', padding: '3px 6px', fontWeight: 700, fontSize: '11px', textAlign: 'right' }}>{row.label}</td>
                  <td style={{ padding: '3px 6px', fontSize: '11px', textAlign: 'right', borderLeft: '1px solid #999', borderTop: '1px solid #e5e5e5' }}>{fmt(row.data?.initial_charge)}</td>
                  <td style={{ padding: '3px 6px', fontSize: '11px', textAlign: 'right', borderTop: '1px solid #e5e5e5' }}>{fmt(row.data?.adjustment)}</td>
                  <td style={{ padding: '3px 6px', fontSize: '11px', textAlign: 'right', borderTop: '1px solid #e5e5e5', fontWeight: 700 }}>{fmt(row.data?.final)}</td>
                </tr>
              ))}
            </tfoot>
          </table>
        </div>
      </div>
    </>
  );
};

export default CheckAvailabilityPreview

